import { Suspense, useMemo } from 'react';
import { ScrollView } from 'react-native';
import {
  Flex,
  RemoteImage,
  Spinner,
  StackScreen,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import { AnalyticsScreen } from 'shared/src/context';
import { IconShortcut } from 'shared/src/component';
import { ICON_VIEW_TYPE, type IconSummary } from 'shared/src/api';
import { useHandleBackPressed, useOpenDeeplink } from 'shared/src/hooks';
import { useIconsAll } from './queries/useIconsAll';
import { useLogClickIcon } from '../Category/hooks/useLogClickIcon';
import { useLogViewServiceMap } from './hooks/useLogViewServiceMap';

const SCREEN = 'OVERVIEW_ICON_ALL';

const FEATURED_COLUMNS = 2;
const FEATURED_ICON_SIZE = 52;
const FIVE_COLUMNS = 5;
const SECTION_HORIZONTAL_PADDING = 12;

const ServiceMapContent = () => {
  useLogViewServiceMap();
  const { data } = useIconsAll();
  const { openDeeplink } = useOpenDeeplink();
  const logClickIcon = useLogClickIcon();

  const handlePress = (icon: IconSummary) => {
    logClickIcon(icon);
    openDeeplink(icon.item.deeplink);
  };

  return (
    <ScrollView contentContainerStyle={{ paddingBottom: 100 }}>
      {data.section_list.map(section => {
        // view_type이 없으면 FIVE_COL_ICON으로 처리. 섹션은 동일 타입으로 구성됨.
        const isFeatured =
          section.icon_list[0]?.item.view_type ===
          ICON_VIEW_TYPE.TWO_COL_FEATURED_ICON;

        return (
          <Flex key={section.section_title} backgroundColor="white">
            <Flex padding={{ left: 16, top: 24, bottom: 8 }}>
              <Typography type="subtitle1" color="content_primary">
                {section.section_title}
              </Typography>
            </Flex>
            {isFeatured ? (
              <FeaturedShortcutList
                icons={section.icon_list}
                onPressIcon={handlePress}
              />
            ) : (
              <FiveColShortcutList
                icons={section.icon_list}
                onPressIcon={handlePress}
              />
            )}
          </Flex>
        );
      })}
    </ScrollView>
  );
};

/** 아이콘 + 라벨 고정 5열 그리드 (FIVE_COL_ICON, view_type 미지정 기본). */
const FiveColShortcutList = ({
  icons,
  onPressIcon,
}: {
  icons: IconSummary[];
  onPressIcon: (icon: IconSummary) => void;
}) => {
  const { width: screenWidth } = useScreenSize();
  const itemWidth =
    (screenWidth - SECTION_HORIZONTAL_PADDING * 2) / FIVE_COLUMNS;

  const rows = useMemo<IconSummary[][]>(() => {
    const result: IconSummary[][] = [];
    for (let i = 0; i < icons.length; i += FIVE_COLUMNS) {
      result.push(icons.slice(i, i + FIVE_COLUMNS));
    }
    return result;
  }, [icons]);

  return (
    <Flex gap={16} padding={{ horizontal: SECTION_HORIZONTAL_PADDING, vertical: 12 }}>
      {rows.map(row => (
        <Flex.Row key={row[0].item.name}>
          {row.map(icon => (
            <IconShortcut
              key={icon.item.name}
              imageUri={icon.item.representative_image}
              name={icon.item.name}
              width={itemWidth}
              onPress={() => onPressIcon(icon)}
            />
          ))}
        </Flex.Row>
      ))}
    </Flex>
  );
};

/** 아이콘(좌) + 제목 + 설명 2열 리스트 (TWO_COL_FEATURED_ICON). */
const FeaturedShortcutList = ({
  icons,
  onPressIcon,
}: {
  icons: IconSummary[];
  onPressIcon: (icon: IconSummary) => void;
}) => {
  const rows = useMemo<IconSummary[][]>(() => {
    const result: IconSummary[][] = [];
    for (let i = 0; i < icons.length; i += FEATURED_COLUMNS) {
      result.push(icons.slice(i, i + FEATURED_COLUMNS));
    }
    return result;
  }, [icons]);

  return (
    <Flex gap={16} padding={{ horizontal: 12, vertical: 12 }}>
      {rows.map(row => (
        <Flex.Row key={row[0].item.name} gap={12}>
          {row.map(icon => (
            <FeaturedShortcutItem
              key={icon.item.name}
              icon={icon}
              onPress={() => onPressIcon(icon)}
            />
          ))}
          {Array.from({ length: FEATURED_COLUMNS - row.length }).map((_, i) => (
            <Flex key={`pad-${i}`} flex={1} />
          ))}
        </Flex.Row>
      ))}
    </Flex>
  );
};

const FeaturedShortcutItem = ({
  icon,
  onPress,
}: {
  icon: IconSummary;
  onPress: () => void;
}) => (
  <Flex.Row flex={1} gap={8} alignItems="center" onPress={onPress}>
    <RemoteImage
      uri={icon.item.representative_image}
      width={FEATURED_ICON_SIZE}
      height={FEATURED_ICON_SIZE}
    />
    <Flex flex={1} gap={2}>
      <Typography type="subtitle4" color="content_primary" numberOfLines={1}>
        {icon.item.name}
      </Typography>
      {icon.item.description != null && (
        <Typography type="body4" color="content_tertiary" numberOfLines={1}>
          {icon.item.description}
        </Typography>
      )}
    </Flex>
  </Flex.Row>
);

export const ServiceMapScreen = () => {
  const { showBackButton, onPressBack } = useHandleBackPressed();

  return (
    <AnalyticsScreen screen={SCREEN}>
      <StackScreen
        title={'전체메뉴'}
        hideBackButton={!showBackButton}
        onPressBack={onPressBack}
      >
        <Suspense
          fallback={
            <Flex.Center flex={1}>
              <Spinner />
            </Flex.Center>
          }
        >
          <ServiceMapContent />
        </Suspense>
      </StackScreen>
    </AnalyticsScreen>
  );
};
